import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Siren, Clock, CalendarDays, Tag, LogOut, Handshake, List, Pencil, XCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { RESTAURANT, MATCH } from '@/constants/testIds';
import { PRODUCT_CATEGORIES, FORMAT_CATEGORIES } from '@/constants/products';
import SettingsButton from '@/components/SettingsButton';
import PaymentDialog from '@/components/PaymentDialog';
import ProductAutocomplete from '@/components/ProductAutocomplete';
import { QRCodeSVG } from 'qrcode.react';
import NotificationBell from '@/components/NotificationBell';
import MyCartsButton from '@/components/MyCartsButton';
import ChatButton, { openChat } from '@/components/ChatButton';
import PeriodFilter, { EMPTY_PERIOD, inPeriod, yearsOf } from '@/components/PeriodFilter';
import GeoFilter, { EMPTY_GEO, inGeo, isGeoActive } from '@/components/GeoFilter';
import LanguageSelector from '@/components/LanguageSelector';
import AdminLink from '@/components/AdminLink';
import SupportButton from '@/components/SupportButton';
import InstallAppButton from '@/components/InstallAppButton';
import DemoBanner from '@/components/DemoBanner';
import DemoTour from '@/components/DemoTour';
import CollapsibleFilters from '@/components/CollapsibleFilters';
import { formatMoney } from '@/lib/locale';
import { useTranslation } from 'react-i18next';
import { useDeepLink, highlightClass, deliveryLabel } from '@/lib/deepLink';
import { computeOrderTotal, vatLabel } from '@/lib/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RestaurantDashboard = () => {
  const { user, logout, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState(() => { const q = new URLSearchParams(window.location.search).get('tab'); return ['crear','subastas','mercadillo','catalogos','matches'].includes(q) ? q : 'crear'; });
  const highlightId = useDeepLink(setActiveTab, ['crear', 'subastas', 'mercadillo', 'catalogos', 'matches']);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [myAuctions, setMyAuctions] = useState([]);
  const [surplus, setSurplus] = useState([]);
  const [matches, setMatches] = useState([]);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [matchData, setMatchData] = useState(null);
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [editingAuctionId, setEditingAuctionId] = useState(null);
  const [confirmCloseId, setConfirmCloseId] = useState(null);
  const [pendingPayment, setPendingPayment] = useState(null); // { type: 'bid'|'surplus', id, sourceObj }
  const [purchaseSurplus, setPurchaseSurplus] = useState(null); // partial-purchase dialog state
  const [purchaseQty, setPurchaseQty] = useState('');
  const [sellers, setSellers] = useState([]);
  const [providerFilters, setProviderFilters] = useState({ query: '', category: 'all', maxDistance: '', minRating: 'all' });
  const [auctionFilters, setAuctionFilters] = useState({ status: 'all', urgency: 'all' });
  const [auctionPeriod, setAuctionPeriod] = useState(EMPTY_PERIOD);
  const [matchPeriod, setMatchPeriod] = useState(EMPTY_PERIOD);
  const [surplusQuery, setSurplusQuery] = useState('');
  const [surplusDeliveryFilter, setSurplusDeliveryFilter] = useState('all');
  const [surplusGeo, setSurplusGeo] = useState(EMPTY_GEO);
  const [providerGeo, setProviderGeo] = useState(EMPTY_GEO);
  const { t } = useTranslation();
  const [matchStatusFilter, setMatchStatusFilter] = useState('pending'); // 'all' | 'pending' | 'closed'
  const [confirmCancelMatchId, setConfirmCancelMatchId] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [reviewingMatch, setReviewingMatch] = useState(null); // full match object
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  
  const [auctionForm, setAuctionForm] = useState({
    product: '',
    quantity: '',
    format: '',
    urgency: '',
    delivery_deadline: '',
    auction_closes_at: '',
    frequency: 'weekly',
    duration_weeks: 4,
    delivery_mode: 'either'
  });

  // Convert a Date object to the "YYYY-MM-DDTHH:mm" string that <input type="datetime-local"> expects
  const toLocalInput = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // Suggested defaults per urgency
  const defaultTimes = (urgency) => {
    const now = new Date();
    if (urgency === 'sos') {
      const close = new Date(now.getTime() + 30 * 60 * 1000);       // +30 min
      const delivery = new Date(now.getTime() + 2 * 60 * 60 * 1000); // +2 h
      return { auction_closes_at: toLocalInput(close), delivery_deadline: toLocalInput(delivery) };
    }
    if (urgency === 'tomorrow') {
      const closeD = new Date(now); closeD.setHours(22, 0, 0, 0);
      if (closeD <= now) closeD.setDate(closeD.getDate() + 1);
      const delivery = new Date(now); delivery.setDate(delivery.getDate() + 1); delivery.setHours(12, 0, 0, 0);
      return { auction_closes_at: toLocalInput(closeD), delivery_deadline: toLocalInput(delivery) };
    }
    // weekly
    const close = new Date(now.getTime() + 48 * 60 * 60 * 1000);
    const delivery = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return { auction_closes_at: toLocalInput(close), delivery_deadline: toLocalInput(delivery) };
  };

  useEffect(() => {
    if (activeTab === 'subastas') {
      fetchMyAuctions();
    } else if (activeTab === 'mercadillo') {
      fetchSurplus();
    } else if (activeTab === 'matches') {
      fetchMatches();
    } else if (activeTab === 'catalogos') {
      fetchSellers();
    }
  }, [activeTab]);

  const fetchSellers = async () => {
    try {
      const response = await axios.get(`${API}/sellers/browse`, {
        headers: { Authorization: getAuthHeader() }
      });
      setSellers(response.data);
    } catch (error) {
      console.error('Error fetching sellers:', error);
    }
  };

  const fetchMyAuctions = async () => {
    try {
      const response = await axios.get(`${API}/auctions/my`, {
        headers: { Authorization: getAuthHeader() }
      });
      setMyAuctions(response.data);
    } catch (error) {
      console.error('Error fetching auctions:', error);
    }
  };

  const fetchSurplus = async () => {
    try {
      const response = await axios.get(`${API}/surplus`, {
        headers: { Authorization: getAuthHeader() }
      });
      setSurplus(response.data);
    } catch (error) {
      console.error('Error fetching surplus:', error);
    }
  };

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches/my`, {
        headers: { Authorization: getAuthHeader() }
      });
      setMatches(response.data);
    } catch (error) {
      console.error('Error fetching matches:', error);
    }
  };

  const deleteMany = async (url, ids, okMsg) => {
    const results = await Promise.allSettled(ids.map((id) => axios.delete(`${API}/${url}/${id}`, { headers: { Authorization: getAuthHeader() } })));
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    if (ok) toast.success(`${ok} ${okMsg}`);
    if (ok < ids.length) toast.error(`${ids.length - ok} no se pudieron borrar`);
  };

  const handleCancelMatch = async (matchId) => {
    try {
      await axios.post(`${API}/matches/${matchId}/cancel`, { reason: cancelReason.trim() || null }, {
        headers: { Authorization: getAuthHeader() }
      });
      toast.success('Pedido cancelado');
      setConfirmCancelMatchId(null);
      setCancelReason('');
      fetchMatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cancelar el pedido');
    }
  };

  const handleSubmitReview = async () => {
    if (!reviewingMatch || reviewRating < 1) return;
    try {
      await axios.post(`${API}/matches/${reviewingMatch.id}/review`, { rating: reviewRating, comment: reviewComment.trim() || null }, {
        headers: { Authorization: getAuthHeader() }
      });
      toast.success('Reseña enviada. ¡Gracias!');
      setReviewingMatch(null);
      setReviewRating(0);
      setReviewComment('');
      fetchMatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar la reseña');
    }
  };

  const fetchAuctionBids = async (auctionId) => {
    try {
      const response = await axios.get(`${API}/bids/${auctionId}`, {
        headers: { Authorization: getAuthHeader() }
      });
      setBids(response.data);
    } catch (error) {
      toast.error('Error al cargar las pujas');
    }
  };

  const openModal = (type) => {
    setModalType(type);
    const times = defaultTimes(type);
    setAuctionForm({
      product: '',
      quantity: '',
      format: '',
      urgency: type,
      frequency: 'weekly',
      duration_weeks: 4,
      delivery_mode: 'either',
      ...times
    });
    setShowModal(true);
  };

  const handleCreateAuction = async () => {
    try {
      const payload = {
        product: auctionForm.product,
        quantity: parseInt(auctionForm.quantity),
        format: auctionForm.format,
        urgency: auctionForm.urgency,
        delivery_deadline: new Date(auctionForm.delivery_deadline).toISOString(),
        auction_closes_at: new Date(auctionForm.auction_closes_at).toISOString(),
        delivery_mode: auctionForm.delivery_mode
      };
      // Only send frequency/duration for weekly contracts
      if (auctionForm.urgency === 'weekly') {
        payload.frequency = auctionForm.frequency;
        payload.duration_weeks = parseInt(auctionForm.duration_weeks);
      }
      if (editingAuctionId) {
        // PATCH existing auction (product and urgency are not editable)
        const editPayload = {
          quantity: payload.quantity,
          format: payload.format,
          delivery_deadline: payload.delivery_deadline,
          auction_closes_at: payload.auction_closes_at,
          delivery_mode: payload.delivery_mode
        };
        if (auctionForm.urgency === 'weekly') {
          editPayload.frequency = payload.frequency;
          editPayload.duration_weeks = payload.duration_weeks;
        }
        await axios.patch(`${API}/auctions/${editingAuctionId}`, editPayload, {
          headers: { Authorization: getAuthHeader() }
        });
        toast.success('Subasta actualizada');
      } else {
        await axios.post(`${API}/auctions`, payload, {
          headers: { Authorization: getAuthHeader() }
        });
        toast.success('¡Subasta creada! Los proveedores ya pueden pujar.');
      }
      setShowModal(false);
      setEditingAuctionId(null);
      setAuctionForm({ product: '', quantity: '', format: '', urgency: '', delivery_deadline: '', auction_closes_at: '', frequency: 'weekly', duration_weeks: 4, delivery_mode: 'either' });
      fetchMyAuctions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar la subasta');
    }
  };

  const openEditModal = (auction) => {
    setEditingAuctionId(auction.id);
    setModalType(auction.urgency);
    setAuctionForm({
      product: auction.product,
      quantity: String(auction.quantity),
      format: auction.format,
      urgency: auction.urgency,
      frequency: auction.frequency || 'weekly',
      duration_weeks: auction.duration_weeks || 4,
      delivery_mode: auction.delivery_mode || 'either',
      delivery_deadline: toLocalInput(new Date(auction.delivery_deadline)),
      auction_closes_at: toLocalInput(new Date(auction.auction_closes_at))
    });
    setShowModal(true);
  };

  const handleCloseAuction = async (auctionId) => {
    try {
      await axios.post(`${API}/auctions/${auctionId}/close`, {}, {
        headers: { Authorization: getAuthHeader() }
      });
      toast.success('Subasta cerrada');
      setConfirmCloseId(null);
      fetchMyAuctions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar la subasta');
    }
  };

  // Step 1: user clicked "Aceptar" on a bid → open payment dialog
  const handleAcceptBid = (bid) => {
    setPendingPayment({ kind: 'bid', id: bid.id });
  };

  // Step 1: user clicked "Aceptar" on a surplus → open payment dialog
  const handleAcceptSurplus = (surplusItem) => {
    setPendingPayment({ kind: 'surplus', id: surplusItem.id });
  };

  // Step 2: after user confirms payment in PaymentDialog, actually create the match
  const handlePaymentConfirmed = async () => {
    if (!pendingPayment) return;
    try {
      let response;
      if (pendingPayment.kind === 'bid') {
        response = await axios.post(
          `${API}/matches/accept-bid`,
          { bid_id: pendingPayment.id },
          { headers: { Authorization: getAuthHeader() } }
        );
        fetchMyAuctions();
      } else {
        const payload = { surplus_id: pendingPayment.id };
        if (pendingPayment.quantity) payload.quantity = pendingPayment.quantity;
        if (pendingPayment.delivery_mode) payload.delivery_mode = pendingPayment.delivery_mode;
        response = await axios.post(
          `${API}/matches/accept-surplus`,
          payload,
          { headers: { Authorization: getAuthHeader() } }
        );
        fetchSurplus();
      }
      setPendingPayment(null);
      setMatchData(response.data);
      setShowMatchDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al confirmar la aceptación');
    }
  };

  const urgencyConfig = {
    sos: { label: 'S.O.S. URGENTE', subLabel: 'Necesidad inmediata', icon: Siren, color: 'red' },
    tomorrow: { label: 'Programada', subLabel: 'Elige hora de entrega y cierre', icon: Clock, color: 'orange' },
    weekly: { label: 'Peticiones de Oferta', subLabel: 'Petición recurrente o gran volumen', icon: CalendarDays, color: 'blue' }
  };

  const frequencyLabels = {
    one_time: 'Puntual',
    weekly: 'Semanal',
    biweekly: 'Quincenal',
    monthly: 'Mensual'
  };

  const deliveryModeLabels = {
    delivery: 'Con transporte',
    pickup: 'Recogida',
    either: 'Flexible'
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid={RESTAURANT.dashboard}>
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-3 sm:px-4 py-2.5 sm:py-4 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="text-base sm:text-2xl font-bold text-slate-900 tracking-tight truncate" translate="no" data-testid="header-business-name">
              {user?.business_name}
            </h1>
            <p className="text-xs sm:text-sm text-slate-600">Restaurante</p>
          </div>
          <div className="flex items-center gap-0.5 sm:gap-2 shrink-0">
            <div className="hidden sm:flex items-center gap-2">
              <InstallAppButton variant="icon" />
              <AdminLink />
              <SupportButton />
              <LanguageSelector />
            </div>
            <div className="sm:hidden"><AdminLink /></div>
            <MyCartsButton />
            <ChatButton />
            <NotificationBell />
            <SettingsButton />
            <Button
              onClick={logout}
              variant="ghost"
              size="sm"
              data-testid="logout-button"
              className="hidden sm:inline-flex text-slate-600 hover:text-slate-900"
            >
              <LogOut size={20} />
            </Button>
          </div>
        </div>
      </header>
      <DemoBanner />
      <DemoTour role="buyer" activeTab={activeTab} setActiveTab={setActiveTab} steps={[
        { title: t('tour.buyer.intro.title'), desc: t('tour.buyer.intro.desc'), tip: t('tour.buyer.intro.tip') },
        ...['crear', 'subastas', 'mercadillo', 'catalogos', 'matches'].map((tab) => ({ tab, title: t(`tour.buyer.${tab}.title`), desc: t(`tour.buyer.${tab}.desc`), tip: tab === 'crear' ? t('tour.buyer.crear.tip') : undefined })),
      ]} />

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 mb-5 sm:mb-8 h-auto p-1">
            <TabsTrigger value="crear" data-tour="tab-crear" data-testid={RESTAURANT.auctionsTab} className="text-[11px] sm:text-sm px-0.5 sm:px-3 py-2.5 sm:py-2 truncate">{t('nav.create')}</TabsTrigger>
            <TabsTrigger value="subastas" data-tour="tab-subastas" className="text-[11px] sm:text-sm px-0.5 sm:px-3 py-2.5 sm:py-2 truncate">{t('nav.auctions')}</TabsTrigger>
            <TabsTrigger value="mercadillo" data-tour="tab-mercadillo" data-testid={RESTAURANT.surplusTab} className="text-[11px] sm:text-sm px-0.5 sm:px-3 py-2.5 sm:py-2 truncate">{t('nav.surplus')}</TabsTrigger>
            <TabsTrigger value="catalogos" data-tour="tab-catalogos" data-testid="catalogs-tab" className="text-[11px] sm:text-sm px-0.5 sm:px-3 py-2.5 sm:py-2 truncate">{t('nav.providers')}</TabsTrigger>
            <TabsTrigger value="matches" data-tour="tab-matches" data-testid={RESTAURANT.matchesTab} className="text-[11px] sm:text-sm px-0.5 sm:px-3 py-2.5 sm:py-2 truncate">{t('nav.matches')}</TabsTrigger>
          </TabsList>

          {/* Create Auction Tab */}
          <TabsContent value="crear" className="space-y-6">
            <div className="space-y-6">
              {/* S.O.S. Button */}
              <button
                onClick={() => openModal('sos')}
                data-testid={RESTAURANT.sosButton}
                className="w-full bg-red-500 hover:bg-red-600 text-white rounded-lg p-6 transition-all hover:-translate-y-px shadow-sm hover:shadow-md pulse-ring fade-in-up"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Siren size={32} />
                    <div className="text-left">
                      <h3 className="text-xl sm:text-2xl font-bold">S.O.S. URGENTE</h3>
                      <p className="text-sm opacity-90">Necesidad inmediata</p>
                    </div>
                  </div>
                  <div className="bg-white/20 px-4 py-2 rounded-lg">
                    <p className="text-xs font-medium">¡AHORA!</p>
                  </div>
                </div>
              </button>

              {/* Tomorrow Button */}
              <button
                onClick={() => openModal('tomorrow')}
                data-testid={RESTAURANT.tomorrowButton}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-lg p-6 transition-all hover:-translate-y-px shadow-sm hover:shadow-md fade-in-up-delay-1"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Clock size={32} />
                    <div className="text-left">
                      <h3 className="text-xl sm:text-2xl font-bold">Programada</h3>
                      <p className="text-sm opacity-90">Tú eliges entrega y cierre</p>
                    </div>
                  </div>
                </div>
              </button>

              {/* Weekly Contract Button */}
              <button
                onClick={() => openModal('weekly')}
                data-testid={RESTAURANT.weeklyButton}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-lg p-6 transition-all hover:-translate-y-px shadow-sm hover:shadow-md fade-in-up-delay-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <CalendarDays size={32} />
                    <div className="text-left">
                      <h3 className="text-xl sm:text-2xl font-bold">Peticiones de Oferta</h3>
                      <p className="text-sm opacity-90">Compra recurrente o gran volumen</p>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          </TabsContent>

          {/* My Auctions Tab */}
          <TabsContent value="subastas">
            {/* Filters */}
            <CollapsibleFilters testId="auction-filters-toggle" active={auctionFilters.status !== 'all' || auctionFilters.urgency !== 'all'}>
            <div className="flex flex-wrap items-end gap-2" data-testid="auction-filters">
              <div className="min-w-[130px]">
                <Label className="text-xs text-slate-500">Estado</Label>
                <Select value={auctionFilters.status} onValueChange={(v) => setAuctionFilters({ ...auctionFilters, status: v })}>
                  <SelectTrigger data-testid="auction-filter-status" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="active">Activas</SelectItem>
                    <SelectItem value="closed">Pasadas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[160px]">
                <Label className="text-xs text-slate-500">Tipo</Label>
                <Select value={auctionFilters.urgency} onValueChange={(v) => setAuctionFilters({ ...auctionFilters, urgency: v })}>
                  <SelectTrigger data-testid="auction-filter-urgency" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los tipos</SelectItem>
                    <SelectItem value="sos">S.O.S. urgente</SelectItem>
                    <SelectItem value="tomorrow">Programada</SelectItem>
                    <SelectItem value="weekly">Peticiones de Oferta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(auctionFilters.status !== 'all' || auctionFilters.urgency !== 'all') && (
                <Button variant="outline" size="sm" data-testid="auction-filter-clear" onClick={() => setAuctionFilters({ status: 'all', urgency: 'all' })}>
                  Limpiar
                </Button>
              )}
            </div>
            </CollapsibleFilters>

            <div className="space-y-4">
              {(() => {
                const filtered = myAuctions.filter((a) => {
                  if (auctionFilters.status === 'active' && a.status !== 'active') return false;
                  if (auctionFilters.status === 'closed' && a.status === 'active') return false;
                  if (auctionFilters.urgency !== 'all' && a.urgency !== auctionFilters.urgency) return false;
                  return inPeriod(a.created_at, auctionPeriod);
                });
                const closedIds = filtered.filter((a) => a.status !== 'active').map((a) => a.id);
                return (
                  <>
                    <PeriodFilter
                      testPrefix="auction"
                      value={auctionPeriod}
                      onChange={setAuctionPeriod}
                      years={yearsOf(myAuctions)}
                      deletableCount={closedIds.length}
                      deleteLabel="Borrar pasadas"
                      onDeleteClosed={async () => { await deleteMany('auctions', closedIds, 'subastas borradas'); fetchMyAuctions(); }}
                    />
                    {filtered.length === 0 ? (
                    <div className="text-center py-12" data-testid="auctions-empty">
                      <List size={48} className="mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">
                        {myAuctions.length === 0 ? 'No tienes subastas activas' : 'Ninguna subasta coincide con los filtros'}
                      </p>
                    </div>
                    ) : filtered.map((auction, index) => {
                  const config = urgencyConfig[auction.urgency];
                  return (
                    <div
                      key={auction.id}
                      id={`deeplink-${auction.id}`}
                      className={`bg-white rounded-lg border border-slate-200 p-5 shadow-sm fade-in-up-delay-${Math.min(index, 3)}${highlightClass(highlightId, auction.id)}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <config.icon size={20} className={`text-${config.color}-500`} />
                            <span className="text-xs font-medium text-slate-500">{config.label}</span>
                          </div>
                          <h3 className="text-lg font-bold text-slate-900">
                            {auction.quantity} {auction.format} de {auction.product}
                          </h3>
                          <p className="text-sm text-slate-600 mt-1">
                            {auction.bids_count} puja{auction.bids_count !== 1 ? 's' : ''}
                          </p>
                          {auction.auction_closes_at && (
                            <p className="text-xs text-slate-500 mt-2">
                              Cierra: {new Date(auction.auction_closes_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                              {' · '}
                              Entrega: {new Date(auction.delivery_deadline).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                          {auction.urgency === 'weekly' && auction.frequency && (
                            <p className="text-xs text-blue-700 mt-1 font-medium">
                              {frequencyLabels[auction.frequency]}
                              {auction.duration_weeks ? ` · ${auction.duration_weeks} semanas` : ''}
                            </p>
                          )}
                          {auction.delivery_mode && (
                            <p className="text-xs text-slate-600 mt-1">
                              🚚 {deliveryModeLabels[auction.delivery_mode] || 'Flexible'}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                              auction.status === 'active'
                                ? 'bg-emerald-100 text-emerald-700'
                                : 'bg-slate-100 text-slate-700'
                            }`}
                          >
                            {auction.status === 'active' ? 'Activa' : 'Cerrada'}
                          </span>
                        </div>
                      </div>
                      {auction.status === 'active' && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {auction.bids_count > 0 && (
                            <Button
                              onClick={() => {
                                setSelectedAuction(auction);
                                fetchAuctionBids(auction.id);
                              }}
                              data-testid="view-bids-button"
                              className="flex-1 min-w-[140px] bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              Ver Pujas ({auction.bids_count})
                            </Button>
                          )}
                          <Button
                            onClick={() => openEditModal(auction)}
                            data-testid="edit-auction-button"
                            variant="outline"
                            className="flex-1 min-w-[100px]"
                          >
                            <Pencil size={16} className="mr-1" />
                            Editar
                          </Button>
                          <Button
                            onClick={() => setConfirmCloseId(auction.id)}
                            data-testid="close-auction-button"
                            variant="outline"
                            className="flex-1 min-w-[100px] text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <XCircle size={16} className="mr-1" />
                            Cerrar
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
                  </>
                );
              })()}
            </div>
          </TabsContent>

          {/* Surplus Marketplace Tab */}
          <TabsContent value="mercadillo">
            <CollapsibleFilters testId="surplus-filters-toggle" active={!!surplusQuery || surplusDeliveryFilter !== 'all' || isGeoActive(surplusGeo)}>
            <div className="flex flex-wrap items-end gap-2" data-testid="surplus-filters">
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs text-slate-500">Buscar</Label>
                <Input
                  data-testid="surplus-search"
                  value={surplusQuery}
                  onChange={(e) => setSurplusQuery(e.target.value)}
                  placeholder="Ej: tomate, sobrestock…"
                  className="h-9"
                />
              </div>
              <GeoFilter testPrefix="surplus" value={surplusGeo} onChange={setSurplusGeo} />
              <div className="min-w-[160px]">
                <Label className="text-xs text-slate-500">Modo de entrega</Label>
                <Select value={surplusDeliveryFilter} onValueChange={setSurplusDeliveryFilter}>
                  <SelectTrigger data-testid="surplus-filter-delivery" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="delivery">Con transporte</SelectItem>
                    <SelectItem value="pickup">Recogida</SelectItem>
                    <SelectItem value="either">Flexible</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(surplusQuery || surplusDeliveryFilter !== 'all' || isGeoActive(surplusGeo)) && (
                <Button variant="outline" size="sm" data-testid="surplus-filter-clear" onClick={() => { setSurplusQuery(''); setSurplusDeliveryFilter('all'); setSurplusGeo(EMPTY_GEO); }}>
                  Limpiar
                </Button>
              )}
            </div>
            </CollapsibleFilters>
            <div className="space-y-4">
              {(() => {
                const q = surplusQuery.trim().toLowerCase();
                const now = Date.now();
                const filtered = surplus.filter((it) => {
                  if (it.active === false || (it.quantity != null && it.quantity <= 0)) return false;
                  if (it.expiry_date && new Date(it.expiry_date).getTime() <= now) return false;
                  if (surplusDeliveryFilter !== 'all' && it.delivery_mode !== surplusDeliveryFilter) return false;
                  if (!inGeo(it, surplusGeo)) return false;
                  if (q) {
                    return `${it.product} ${it.discount_reason || ''} ${it.format || ''}`.toLowerCase().includes(q);
                  }
                  return true;
                });
                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-12" data-testid="surplus-empty">
                      <Tag size={48} className="mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">
                        {surplus.length === 0 ? 'No hay excedentes disponibles' : 'Ningún excedente coincide con tu búsqueda'}
                      </p>
                    </div>
                  );
                }
                return filtered.map((item, index) => (
                  <div
                    key={item.id}
                    className={`bg-white rounded-lg border border-slate-200 p-5 shadow-sm fade-in-up-delay-${Math.min(index, 3)}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-1 rounded">
                            -{item.discount_percentage}%
                          </span>
                          <span className="text-xs text-slate-500">{item.anonymous_name}</span>
                          {item.delivery_mode && (
                            <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                              🚚 {deliveryModeLabels[item.delivery_mode] || 'Flexible'}
                            </span>
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-slate-900">
                          {item.quantity} {item.format} de {item.product}
                        </h3>
                        {item.original_quantity && item.original_quantity !== item.quantity && (
                          <p className="text-xs text-amber-600 mt-1">
                            {item.quantity}/{item.original_quantity} restantes
                          </p>
                        )}
                        {item.discount_reason && (
                          <p className="text-xs text-slate-600 mt-1">
                            Motivo: <span className="font-medium">{item.discount_reason}</span>
                          </p>
                        )}
                        {(item.expiration_from || item.expiration_to) && (
                          <p className="text-xs text-amber-700 mt-1" data-testid={`surplus-expiration-${item.id}`}>
                            📅 Caducidad: {item.expiration_from ? new Date(item.expiration_from).toLocaleDateString('es-ES') : ''}
                            {item.expiration_to ? ` – ${new Date(item.expiration_to).toLocaleDateString('es-ES')}` : ''}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-sm text-slate-400 line-through">
                            €{(item.unit_original_price ?? item.original_price).toFixed(2)}
                          </span>
                          <span className="text-xl font-bold text-emerald-600" data-testid={`surplus-unit-price-${item.id}`}>
                            €{(item.unit_discount_price ?? item.discount_price).toFixed(2)}
                          </span>
                          <span className="text-xs text-slate-500">/ {item.format}</span>
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                            {vatLabel(item.vat_rate || 0)}{item.price_includes_vat && item.vat_rate > 0 ? ' inc.' : ''}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1" data-testid={`surplus-lot-price-${item.id}`}>
                          Lote completo ({item.quantity} {item.format}): €{((item.unit_discount_price ?? item.discount_price) * item.quantity).toFixed(2)}
                        </p>
                        {item.delivery_fee > 0 && item.delivery_mode !== 'pickup' && (
                          <p className="text-xs text-slate-500 mt-1">
                            + €{Number(item.delivery_fee).toFixed(2)} de envío si entrega el proveedor
                          </p>
                        )}
                        {(item.vat_rate > 0 || item.delivery_fee > 0) && (() => {
                          const totals = computeOrderTotal({
                            unit_price: item.unit_discount_price ?? item.discount_price,
                            quantity: 1,
                            vat_rate: item.vat_rate || 0,
                            price_includes_vat: item.price_includes_vat,
                            delivery_fee: item.delivery_mode !== 'pickup' ? (item.delivery_fee || 0) : 0,
                          });
                          return (
                            <p className="text-xs text-emerald-700 font-semibold mt-1" data-testid={`surplus-total-${item.id}`}>
                              Total estimado / {item.format}: €{totals.total.toFixed(2)}
                            </p>
                          );
                        })()}
                        {(item.pickup_address || item.pickup_schedule) && item.delivery_mode !== 'delivery' && (
                          <div className="mt-2 text-xs text-slate-600">
                            {item.pickup_address && <p>📍 {item.pickup_address}</p>}
                            {item.pickup_schedule && <p>🕒 {item.pickup_schedule}</p>}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => setPurchaseSurplus(item)}
                      data-testid="accept-surplus-button"
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      Comprar
                    </Button>
                  </div>
                ));
              })()}
            </div>
          </TabsContent>

          {/* Providers tab — sellers within the buyer's search radius */}
          <TabsContent value="catalogos" data-testid="providers-content">
            {/* Filters */}
            <CollapsibleFilters testId="providers-filters-toggle" active={!!(providerFilters.query || providerFilters.category !== 'all' || providerFilters.maxDistance || providerFilters.minRating !== 'all' || isGeoActive(providerGeo))}>
            <div className="flex flex-wrap items-end gap-2" data-testid="providers-filters">
              <div className="flex-1 min-w-[160px]">
                <Label className="text-xs text-slate-500">Buscar</Label>
                <Input
                  data-testid="provider-filter-query"
                  value={providerFilters.query}
                  onChange={(e) => setProviderFilters({ ...providerFilters, query: e.target.value })}
                  placeholder="Nombre, producto o categoría (ej: tomate, verduras)"
                  className="h-9"
                />
              </div>
              <div className="min-w-[140px]">
                <Label className="text-xs text-slate-500">Categoría</Label>
                <Select value={providerFilters.category} onValueChange={(v) => setProviderFilters({ ...providerFilters, category: v })}>
                  <SelectTrigger data-testid="provider-filter-category" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {Array.from(new Set(sellers.flatMap((s) => s.categories || []))).sort().map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[120px]">
                <Label className="text-xs text-slate-500">Máx. km</Label>
                <Input
                  data-testid="provider-filter-distance"
                  type="number"
                  min="0"
                  value={providerFilters.maxDistance}
                  onChange={(e) => setProviderFilters({ ...providerFilters, maxDistance: e.target.value })}
                  placeholder="—"
                  className="h-9"
                />
              </div>
              <div className="min-w-[140px]">
                <Label className="text-xs text-slate-500">Puntuación mínima</Label>
                <Select value={providerFilters.minRating} onValueChange={(v) => setProviderFilters({ ...providerFilters, minRating: v })}>
                  <SelectTrigger data-testid="provider-filter-rating" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Cualquiera</SelectItem>
                    <SelectItem value="3">★★★ o más</SelectItem>
                    <SelectItem value="4">★★★★ o más</SelectItem>
                    <SelectItem value="5">★★★★★</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <GeoFilter testPrefix="provider" value={providerGeo} onChange={setProviderGeo} />
              {(providerFilters.query || providerFilters.category !== 'all' || providerFilters.maxDistance || providerFilters.minRating !== 'all' || isGeoActive(providerGeo)) && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="provider-filter-clear"
                  onClick={() => { setProviderFilters({ query: '', category: 'all', maxDistance: '', minRating: 'all' }); setProviderGeo(EMPTY_GEO); }}
                >
                  Limpiar
                </Button>
              )}
            </div>
            </CollapsibleFilters>

            {(() => {
              const q = providerFilters.query.trim().toLowerCase();
              const maxKm = providerFilters.maxDistance ? Number(providerFilters.maxDistance) : null;
              const minRating = providerFilters.minRating !== 'all' ? Number(providerFilters.minRating) : null;
              const filtered = sellers.filter((s) => {
                if (!inGeo(s, providerGeo)) return false;
                if (providerFilters.category !== 'all' && !(s.categories || []).includes(providerFilters.category)) return false;
                if (maxKm != null && !Number.isNaN(maxKm) && s.distance_km != null && s.distance_km > maxKm) return false;
                if (minRating != null && (s.rating_avg == null || s.rating_avg < minRating)) return false;
                if (q) {
                  const hay = [
                    s.business_name || '',
                    s.description || '',
                    ...(s.categories || []),
                    ...(s.products || []),
                  ].join(' ').toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                return true;
              });
              if (filtered.length === 0) {
                return (
                  <div className="text-center py-12" data-testid="providers-empty">
                    <Tag size={48} className="mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">
                      {sellers.length === 0
                        ? 'No hay proveedores dentro de tu radio de búsqueda'
                        : 'Ningún proveedor coincide con los filtros'}
                    </p>
                    {sellers.length === 0 && (
                      <p className="text-xs text-slate-400 mt-2">Amplía tu radio en Ajustes para ver más proveedores.</p>
                    )}
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="providers-grid">
                  {filtered.map((s) => (
                  <a
                    key={s.id}
                    href={`/proveedor/${s.id}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`provider-card-${s.id}`}
                    className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden hover:shadow-md hover:border-emerald-300 transition-all duration-200"
                  >
                    <div className="h-20 relative" style={{ background: s.cover_url ? undefined : 'linear-gradient(135deg,#059669,#10b981)' }}>
                      {s.cover_url && (
                        <img src={s.cover_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="p-3 -mt-8 relative">
                      <div className="w-14 h-14 rounded-lg bg-white border border-slate-200 overflow-hidden flex items-center justify-center shadow-sm">
                        {s.logo_url ? (
                          <img src={s.logo_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <Tag size={22} className="text-emerald-600" />
                        )}
                      </div>
                      <h3 className="mt-2 text-sm font-bold text-slate-900 line-clamp-2 leading-snug">
                        {s.business_name}
                      </h3>
                      {s.rating_avg != null && (
                        <p className="text-xs text-amber-700 font-semibold mt-0.5" data-testid={`provider-rating-${s.id}`}>
                          ★ {s.rating_avg.toFixed(1)} <span className="text-slate-400 font-normal">({s.rating_count})</span>
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {s.distance_km != null && (
                          <span className="text-[11px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-medium">
                            ~{s.distance_km} km
                          </span>
                        )}
                        {s.postal_code && (
                          <span className="text-[11px] text-slate-500">CP {s.postal_code}</span>
                        )}
                      </div>
                      {s.description && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2 leading-snug">{s.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-[11px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                          {s.catalog_count} producto{s.catalog_count === 1 ? '' : 's'}
                        </span>
                        {s.has_catalog_pdf && (
                          <span className="text-[11px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-medium" data-testid={`provider-has-pdf-${s.id}`}>
                            📄 PDF
                          </span>
                        )}
                        {!s.contact_unlocked && (
                          <span className="text-[11px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">🔒 Contacto</span>
                        )}
                      </div>
                      {s.categories?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {s.categories.slice(0, 4).map((c) => (
                            <span key={c} className="text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">
                              {c}
                            </span>
                          ))}
                          {s.categories.length > 4 && (
                            <span className="text-[10px] text-slate-400">+{s.categories.length - 4}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </a>
                  ))}
                </div>
              );
            })()}
          </TabsContent>

          {/* Matches Tab */}
          <TabsContent value="matches">
            {/* Status filter */}
            <div className="mb-4 flex flex-wrap gap-2" data-testid="match-status-filter">
              {[
                { k: 'pending', label: 'Pendientes' },
                { k: 'closed', label: 'Cerrados' },
                { k: 'all', label: 'Todos' },
              ].map((opt) => (
                <Button
                  key={opt.k}
                  size="sm"
                  variant={matchStatusFilter === opt.k ? 'default' : 'outline'}
                  onClick={() => setMatchStatusFilter(opt.k)}
                  data-testid={`match-filter-${opt.k}`}
                  className={matchStatusFilter === opt.k ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
                >
                  {opt.label}
                </Button>
              ))}
            </div>

            <div className="space-y-4">
              {(() => {
                // A match is "pending" only if it has an active pickup flow
                // (pickup_token) and hasn't been picked up nor cancelled yet.
                // Legacy auction/surplus matches without a pickup_token are
                // considered historical and treated as closed.
                const isPending = (m) => !!m.pickup_token && !m.picked_up && !m.cancelled;
                const isClosed = (m) => !isPending(m);
                const filtered = matches.filter((m) => {
                  if (!inPeriod(m.created_at, matchPeriod)) return false;
                  if (matchStatusFilter === 'pending') return isPending(m);
                  if (matchStatusFilter === 'closed') return isClosed(m);
                  return true;
                });
                const closedIds = filtered.filter(isClosed).map((m) => m.id);
                return (
                  <>
                    <PeriodFilter
                      testPrefix="match"
                      value={matchPeriod}
                      onChange={setMatchPeriod}
                      years={yearsOf(matches)}
                      deletableCount={closedIds.length}
                      onDeleteClosed={async () => { await deleteMany('matches', closedIds, 'pedidos borrados'); fetchMatches(); }}
                    />
                    {filtered.length === 0 ? (
                    <div className="text-center py-12" data-testid="matches-empty">
                      <Handshake size={48} className="mx-auto text-slate-300 mb-4" />
                      <p className="text-slate-500">
                        {matches.length === 0
                          ? 'No tienes matches aún'
                          : matchStatusFilter === 'pending' ? 'No tienes pedidos pendientes' : 'No tienes pedidos cerrados'}
                      </p>
                    </div>
                    ) : filtered.map((match, index) => {
                  const isCatalog = match.type === 'catalog_order';
                  const isPickedUp = !!match.picked_up;
                  const isCancelled = !!match.cancelled;
                  const canCancel = !isPickedUp && !isCancelled && match.pickup_token;
                  const typeLabel = match.type === 'auction' ? 'Subasta'
                    : match.type === 'catalog_order' ? 'Pedido de catálogo'
                    : 'Excedente';
                  return (
                    <div
                      key={match.id}
                      id={`deeplink-${match.id}`}
                      data-testid={`match-card-${match.id}`}
                      className={`bg-white rounded-lg border border-slate-200 p-5 shadow-sm fade-in-up-delay-${Math.min(index, 3)}${highlightClass(highlightId, match.id)}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Handshake size={20} className="text-emerald-600" />
                          <span className="text-xs font-medium text-emerald-600">Match Confirmado</span>
                        </div>
                        {isCancelled ? (
                          <span className="text-xs font-semibold bg-red-100 text-red-800 px-2 py-1 rounded" data-testid="match-status-cancelled">
                            Cancelado{match.cancelled_by === 'seller' ? ' por el proveedor' : ''}
                          </span>
                        ) : match.pickup_token ? (
                          isPickedUp ? (
                            <span className="flex items-center gap-1 text-xs font-semibold bg-emerald-100 text-emerald-800 px-2 py-1 rounded" data-testid="match-status-picked">
                              <CheckCircle2 size={12} /> Recogido
                            </span>
                          ) : (
                            <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-2 py-1 rounded" data-testid="match-status-pending">
                              Pendiente de recoger
                            </span>
                          )
                        ) : null}
                      </div>

                      {isCatalog && match.items && match.items.length > 0 ? (
                        <div className="mb-2">
                          <ul className="text-sm text-slate-900 space-y-0.5" data-testid="match-items-list">
                            {match.items.map((it, i) => (
                              <li key={i}><strong>{it.quantity}×</strong> {it.product} <span className="text-slate-500">({it.format})</span></li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <h3 className="text-lg font-bold text-slate-900 mb-2">
                          {match.quantity} {match.format} de {match.product}
                        </h3>
                      )}

                      <div className="bg-slate-50 rounded-lg p-4 mb-3">
                        <p className="text-sm font-medium text-slate-900 mb-1">{match.supplier_name}</p>
                        <p className="text-sm text-slate-600">Tel: {match.supplier_phone}</p>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`chat-match-${match.id}`}
                          onClick={() => openChat({ seller_id: match.supplier_id, context_type: 'match', context_id: match.id })}
                          className="mt-2 h-8 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        >
                          💬 Chat con el proveedor
                        </Button>
                      </div>

                      {match.delivery_mode && (
                        <div className={`rounded-lg p-3 mb-3 border text-sm ${match.delivery_mode === 'delivery' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`match-delivery-${match.id}`}>
                          <p className="font-semibold text-slate-900">🚚 {deliveryLabel(match.delivery_mode)}</p>
                          {match.delivery_mode === 'delivery' && match.delivery_fee > 0 && (
                            <p className="text-xs text-slate-600 mt-0.5">Transporte: €{Number(match.delivery_fee).toFixed(2)} (incluido en el total)</p>
                          )}
                        </div>
                      )}

                      {match.pickup_token && !isPickedUp && !isCancelled && (
                        <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-col items-center mb-3" data-testid="match-pickup-qr-block">
                          <p className="text-xs text-slate-500 mb-2">Muestra este código al proveedor</p>
                          <QRCodeSVG value={match.pickup_token} size={140} data-testid="match-pickup-qr" />
                          <p className="mt-2 font-mono font-bold text-base text-slate-900 tracking-widest" data-testid="match-pickup-token">
                            {match.pickup_token}
                          </p>
                        </div>
                      )}

                      {match.pickup_token && isPickedUp && match.picked_up_at && (
                        <p className="text-xs text-slate-500 mb-3">
                          Entregado el {new Date(match.picked_up_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}

                      {isCancelled && match.cancelled_at && (
                        <div className="mb-3">
                          <p className="text-xs text-slate-500">
                            Cancelado el {new Date(match.cancelled_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {match.cancelled_reason && (
                            <p className="text-xs text-slate-600 mt-1 italic" data-testid="cancelled-reason">
                              Motivo: "{match.cancelled_reason}"
                            </p>
                          )}
                        </div>
                      )}

                      {isPickedUp && match.review && (
                        <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs" data-testid="existing-review">
                          <p className="font-semibold text-amber-900">Tu valoración: {'★'.repeat(match.review.rating)}<span className="text-slate-300">{'★'.repeat(5 - match.review.rating)}</span></p>
                          {match.review.comment && <p className="text-slate-700 mt-0.5 italic">"{match.review.comment}"</p>}
                        </div>
                      )}

                      <div className="flex items-center justify-between text-sm">
                        {match.price != null ? (
                          <span className="text-slate-600">
                            Precio: €{match.price?.toFixed(2)}{!isCatalog && match.format ? ` / ${match.format}` : ''}
                            {!isCatalog && (match.quantity > 1 || match.delivery_fee > 0) && (
                              <span className="ml-1 font-semibold text-slate-800" data-testid={`match-total-${match.id}`}>· Total €{(match.price * match.quantity + (match.delivery_mode === 'delivery' ? Number(match.delivery_fee || 0) : 0)).toFixed(2)}</span>
                            )}
                            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                              {vatLabel(match.vat_rate || 0)}{match.price_includes_vat && match.vat_rate > 0 ? ' inc.' : ''}
                            </span>
                          </span>
                        ) : <span />}
                        <span className="text-slate-500 text-xs">{typeLabel}</span>
                      </div>
                      {match.delivery_mode === 'delivery' && match.delivery_fee > 0 && (
                        <p className="text-xs text-slate-500 mt-1">
                          + {formatMoney(match.delivery_fee, match.currency)} de transporte del proveedor
                        </p>
                      )}
                      {match.reverse_charge && (
                        <p className="text-xs mt-2 px-2 py-1 rounded bg-indigo-50 text-indigo-800 border border-indigo-200" data-testid={`match-reverse-charge-${match.id}`}>
                          🇪🇺 {t('common.reverseCharge')} · {match.seller_country} → {match.buyer_country}. {t('common.reverseChargeHint')}
                        </p>
                      )}

                      {canCancel && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setConfirmCancelMatchId(match.id)}
                          data-testid={`cancel-match-${match.id}`}
                          className="w-full mt-3 border-red-200 text-red-700 hover:bg-red-50"
                        >
                          Cancelar pedido
                        </Button>
                      )}

                      {isPickedUp && !match.review && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setReviewingMatch(match); setReviewRating(0); setReviewComment(''); }}
                          data-testid={`review-match-${match.id}`}
                          className="w-full mt-3 border-amber-200 text-amber-700 hover:bg-amber-50"
                        >
                          ⭐ Valorar al proveedor
                        </Button>
                      )}
                    </div>
                  );
                })}
                  </>
                );
              })()}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create / Edit Auction Modal */}
      <Dialog open={showModal} onOpenChange={(open) => {
        setShowModal(open);
        if (!open) {
          setEditingAuctionId(null);
        }
      }}>
        <DialogContent data-testid={RESTAURANT.createAuctionModal} className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAuctionId ? 'Editar subasta' : (modalType && urgencyConfig[modalType]?.label)}
            </DialogTitle>
            <DialogDescription>
              {editingAuctionId ? 'El producto y el tipo de subasta no se pueden cambiar.' : (modalType && urgencyConfig[modalType]?.subLabel)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Producto *</Label>
              <ProductAutocomplete
                testId={RESTAURANT.productSelect}
                value={auctionForm.product}
                onChange={(v) => setAuctionForm({ ...auctionForm, product: v })}
                disabled={!!editingAuctionId}
                placeholder="Ej: Coca-Cola"
              />
              <p className="text-xs text-slate-500">Escribe libremente; te sugeriremos coincidencias.</p>
            </div>

            <div className="space-y-2">
              <Label>Cantidad *</Label>
              <Input
                data-testid={RESTAURANT.quantityInput}
                type="number"
                min="1"
                value={auctionForm.quantity}
                onChange={(e) => setAuctionForm({ ...auctionForm, quantity: e.target.value })}
                placeholder="Ej: 20"
              />
            </div>

            <div className="space-y-2">
              <Label>Formato / Unidad *</Label>
              <Select
                value={auctionForm.format}
                onValueChange={(value) => setAuctionForm({ ...auctionForm, format: value })}
              >
                <SelectTrigger data-testid={RESTAURANT.formatSelect}>
                  <SelectValue placeholder="Selecciona el formato" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {Object.entries(FORMAT_CATEGORIES).map(([category, items]) => (
                    <SelectGroup key={category}>
                      <SelectLabel className="text-emerald-700 font-semibold">{category}</SelectLabel>
                      {items.map((format) => (
                        <SelectItem key={format} value={format}>
                          {format}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Modo de entrega *</Label>
              <Select
                value={auctionForm.delivery_mode}
                onValueChange={(value) => setAuctionForm({ ...auctionForm, delivery_mode: value })}
              >
                <SelectTrigger data-testid="delivery-mode-select">
                  <SelectValue placeholder="Selecciona modo de entrega" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="delivery">Con transporte (el proveedor entrega)</SelectItem>
                  <SelectItem value="pickup">Recogida (yo recojo)</SelectItem>
                  <SelectItem value="either">Flexible (cualquiera)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {auctionForm.urgency === 'weekly' && (
              <>
                <div className="space-y-2">
                  <Label>Frecuencia de entrega *</Label>
                  <Select
                    value={auctionForm.frequency}
                    onValueChange={(value) => setAuctionForm({ ...auctionForm, frequency: value })}
                  >
                    <SelectTrigger data-testid="frequency-select">
                      <SelectValue placeholder="Selecciona la frecuencia" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one_time">Puntual (una sola entrega)</SelectItem>
                      <SelectItem value="weekly">Semanal</SelectItem>
                      <SelectItem value="biweekly">Quincenal (cada 2 semanas)</SelectItem>
                      <SelectItem value="monthly">Mensual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Duración del contrato *</Label>
                  <Select
                    value={String(auctionForm.duration_weeks)}
                    onValueChange={(value) => setAuctionForm({ ...auctionForm, duration_weeks: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="duration-select">
                      <SelectValue placeholder="Selecciona la duración" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">1 mes (4 semanas)</SelectItem>
                      <SelectItem value="8">2 meses (8 semanas)</SelectItem>
                      <SelectItem value="12">3 meses (12 semanas)</SelectItem>
                      <SelectItem value="24">6 meses (24 semanas)</SelectItem>
                      <SelectItem value="52">1 año (52 semanas)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Cuánto tiempo se repetirá el pedido con la frecuencia elegida.</p>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label>Cierre de la subasta *</Label>
              <Input
                data-testid="auction-close-input"
                type="datetime-local"
                value={auctionForm.auction_closes_at}
                onChange={(e) => setAuctionForm({ ...auctionForm, auction_closes_at: e.target.value })}
              />
              <p className="text-xs text-slate-500">Hasta cuándo los proveedores pueden pujar.</p>
            </div>

            <div className="space-y-2">
              <Label>Hora límite de entrega *</Label>
              <Input
                data-testid="delivery-deadline-input"
                type="datetime-local"
                value={auctionForm.delivery_deadline}
                onChange={(e) => setAuctionForm({ ...auctionForm, delivery_deadline: e.target.value })}
              />
              <p className="text-xs text-slate-500">Cuándo necesitas recibir el producto.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              data-testid={RESTAURANT.submitAuctionButton}
              onClick={handleCreateAuction}
              disabled={!auctionForm.product || !auctionForm.quantity || !auctionForm.format || !auctionForm.delivery_deadline || !auctionForm.auction_closes_at}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {editingAuctionId ? 'Guardar cambios' : 'Crear Subasta'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bids Dialog */}
      <Dialog open={!!selectedAuction} onOpenChange={() => setSelectedAuction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pujas Recibidas</DialogTitle>
            <DialogDescription>
              {selectedAuction?.quantity} {selectedAuction?.format} de {selectedAuction?.product}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4 max-h-96 overflow-y-auto">
            {bids.map((bid) => {
              const totals = computeOrderTotal({
                unit_price: bid.price,
                quantity: selectedAuction?.quantity || 1,
                vat_rate: bid.vat_rate || 0,
                price_includes_vat: bid.price_includes_vat,
                delivery_fee: bid.delivery_mode === 'delivery' ? (bid.delivery_fee || 0) : 0,
              });
              return (
              <div key={bid.id} className="bg-slate-50 rounded-lg p-4" data-testid={`bid-card-${bid.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{bid.anonymous_name}</p>
                    <p className="text-2xl font-bold text-emerald-600">
                      €{bid.price.toFixed(2)}
                      <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-white text-slate-600 align-middle">
                        {vatLabel(bid.vat_rate || 0)}{bid.price_includes_vat && bid.vat_rate > 0 ? ' inc.' : ''}
                      </span>
                    </p>
                    {bid.delivery_mode && (
                      <p className="text-xs text-slate-600 mt-1">
                        🚚 {deliveryModeLabels[bid.delivery_mode] || 'Con transporte'}
                        {bid.delivery_mode === 'delivery' && bid.delivery_fee > 0 && (
                          <span className="ml-1 text-slate-500">· €{Number(bid.delivery_fee).toFixed(2)} envío</span>
                        )}
                      </p>
                    )}
                    <div className="mt-2 text-xs text-slate-700 bg-white border border-slate-200 rounded p-2" data-testid={`bid-totals-${bid.id}`}>
                      <p>Subtotal: <strong>€{totals.base.toFixed(2)}</strong></p>
                      {totals.vat > 0 && <p>IVA ({bid.vat_rate}%): <strong>€{totals.vat.toFixed(2)}</strong></p>}
                      {totals.fee > 0 && <p>Envío: <strong>€{totals.fee.toFixed(2)}</strong></p>}
                      <p className="text-emerald-700 font-bold mt-0.5">Total estimado: €{totals.total.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <Button
                      onClick={() => {
                        handleAcceptBid(bid);
                        setSelectedAuction(null);
                      }}
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      Aceptar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`chat-bid-${bid.id}`}
                      onClick={() => openChat({ seller_id: bid.supplier_id, context_type: 'auction', context_id: selectedAuction?.id })}
                      className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                    >
                      💬 Chat
                    </Button>
                  </div>
                </div>
              </div>
            );})}
          </div>
        </DialogContent>
      </Dialog>

      {/* Match Confirmation Dialog (shown AFTER payment) */}
      <Dialog open={showMatchDialog} onOpenChange={setShowMatchDialog}>
        <DialogContent data-testid={MATCH.confirmationDialog}>
          <DialogHeader>
            <DialogTitle>Pago confirmado</DialogTitle>
            <DialogDescription>
              Aquí tienes los datos del proveedor
            </DialogDescription>
          </DialogHeader>
          {matchData && (
            <div className="space-y-4 py-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm font-medium text-emerald-900 mb-1">Producto</p>
                <p className="text-lg font-bold text-emerald-900">
                  {matchData.quantity} {matchData.format} de {matchData.product}
                </p>
                <p className="text-sm text-emerald-700 mt-2" data-testid="match-price-summary">
                  Precio: €{matchData.price.toFixed(2)} / {matchData.format}
                  {(matchData.quantity > 1 || matchData.delivery_fee > 0) && ` · Total: €${(matchData.total ?? (matchData.price * matchData.quantity + Number(matchData.delivery_fee || 0))).toFixed(2)}`}
                </p>
                {matchData.delivery_mode === 'delivery' && matchData.delivery_fee > 0 && (
                  <p className="text-xs text-emerald-700">Incluye €{Number(matchData.delivery_fee).toFixed(2)} de transporte</p>
                )}
              </div>

              <div className="bg-slate-50 rounded-lg p-4" data-testid={MATCH.supplierContact}>
                <p className="text-sm font-medium text-slate-900 mb-2">Contacto del Proveedor</p>
                <p className="text-lg font-bold text-slate-900">{matchData.supplier_name}</p>
                <p className="text-sm text-slate-600">Tel: {matchData.supplier_phone}</p>
              </div>

              {matchData.delivery_mode && (
                <div
                  data-testid="match-delivery-info"
                  className={`rounded-lg p-4 border ${
                    matchData.delivery_mode === 'delivery'
                      ? 'bg-emerald-50 border-emerald-200'
                      : matchData.delivery_mode === 'pickup'
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <p className="text-xs font-medium text-slate-600 mb-1">Modo de entrega acordado</p>
                  <p className="text-base font-bold text-slate-900">
                    🚚 {deliveryLabel(matchData.delivery_mode)}
                  </p>
                  <p className="text-sm text-slate-700 mt-1">
                    {matchData.delivery_mode === 'delivery'
                      ? `El proveedor te entrega la mercancía${matchData.delivery_fee > 0 ? ` (transporte €${Number(matchData.delivery_fee).toFixed(2)})` : ''}.`
                      : matchData.delivery_mode === 'pickup'
                        ? 'Debes ir a recoger la mercancía al proveedor.'
                        : 'Coordinad con el proveedor cómo hacer la entrega.'}
                  </p>
                </div>
              )}

              <p className="text-xs text-slate-500 text-center">
                RestoSupply es 100% gratis para compradores.
              </p>

              <Button
                data-testid={MATCH.confirmButton}
                onClick={() => {
                  setShowMatchDialog(false);
                  setMatchData(null);
                  toast.success('¡Ya puedes contactar al proveedor!');
                }}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                Entendido
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog (Step 1: shown BEFORE match creation) */}
      <PaymentDialog
        open={!!pendingPayment}
        onOpenChange={(open) => !open && setPendingPayment(null)}
        previewType={pendingPayment?.kind}
        previewId={pendingPayment?.id}
        previewQuantity={pendingPayment?.quantity}
        previewDeliveryMode={pendingPayment?.delivery_mode}
        onPay={handlePaymentConfirmed}
      />
      {/* Confirm Close Auction Dialog */}
      <Dialog open={!!confirmCloseId} onOpenChange={(open) => !open && setConfirmCloseId(null)}>
        <DialogContent data-testid="confirm-close-dialog">
          <DialogHeader>
            <DialogTitle>¿Cerrar subasta?</DialogTitle>
            <DialogDescription>
              Se cerrará la subasta sin generar un match. Los proveedores no podrán seguir pujando. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setConfirmCloseId(null)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => handleCloseAuction(confirmCloseId)}
              data-testid="confirm-close-button"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Sí, cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Buyer surplus-purchase dialog (quantity + accept/counter-offer) */}
      <SurplusPurchaseDialog
        item={purchaseSurplus}
        onClose={() => { setPurchaseSurplus(null); }}
        onAcceptPrice={(qty, deliveryMode) => {
          // Accept seller's price → open payment dialog (fee=€0 anyway)
          setPendingPayment({ kind: 'surplus', id: purchaseSurplus.id, quantity: qty, delivery_mode: deliveryMode });
          setPurchaseSurplus(null);
        }}
        onCounterOffer={async (qty, pricePerUnit, deliveryMode) => {
          try {
            await axios.post(
              `${API}/surplus/offer`,
              { surplus_id: purchaseSurplus.id, quantity: qty, offer_price: pricePerUnit, delivery_mode: deliveryMode },
              { headers: { Authorization: getAuthHeader() } }
            );
            toast.success('Oferta enviada. Recibirás una respuesta del proveedor.');
            setPurchaseSurplus(null);
          } catch (error) {
            toast.error(error.response?.data?.detail || 'No se pudo enviar la oferta');
          }
        }}
      />

      {/* Cancel match confirmation */}
      <Dialog open={!!confirmCancelMatchId} onOpenChange={(open) => { if (!open) { setConfirmCancelMatchId(null); setCancelReason(''); } }}>
        <DialogContent data-testid="confirm-cancel-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>¿Cancelar este pedido?</DialogTitle>
            <DialogDescription>
              El proveedor será notificado. No podrás recogerlo con este código.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3">
            <Label className="text-xs text-slate-500">Motivo (opcional)</Label>
            <Input
              data-testid="cancel-reason-input"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              maxLength={280}
              placeholder="Ej: he cambiado de menú"
              className="mt-1"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => { setConfirmCancelMatchId(null); setCancelReason(''); }} data-testid="cancel-dialog-abort">
              Volver
            </Button>
            <Button
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              onClick={() => handleCancelMatch(confirmCancelMatchId)}
              data-testid="cancel-dialog-confirm"
            >
              Sí, cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!reviewingMatch} onOpenChange={(open) => { if (!open) { setReviewingMatch(null); setReviewRating(0); setReviewComment(''); } }}>
        <DialogContent data-testid="review-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Valorar al proveedor</DialogTitle>
            <DialogDescription>
              {reviewingMatch?.supplier_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="flex items-center justify-center gap-1" data-testid="rating-stars">
              {[1,2,3,4,5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`rating-star-${n}`}
                  onClick={() => setReviewRating(n)}
                  className={`text-3xl transition ${n <= reviewRating ? 'text-amber-400' : 'text-slate-300 hover:text-amber-300'}`}
                >★</button>
              ))}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Comentario (opcional)</Label>
              <textarea
                data-testid="review-comment-input"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="Cuenta cómo fue tu experiencia…"
                className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm resize-none"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setReviewingMatch(null)}>
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={reviewRating < 1}
                onClick={handleSubmitReview}
                data-testid="review-submit-button"
              >
                Enviar reseña
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// -------------------------------------------------------------
// Surplus purchase dialog — quantity + accept seller price OR counter-offer
// -------------------------------------------------------------
const SurplusPurchaseDialog = ({ item, onClose, onAcceptPrice, onCounterOffer }) => {
  const [qty, setQty] = useState('');
  const [mode, setMode] = useState('accept'); // "accept" | "offer"
  const [offerPricePerUnit, setOfferPricePerUnit] = useState('');
  const [deliveryChoice, setDeliveryChoice] = useState('pickup');

  useEffect(() => {
    if (item) {
      setQty(String(item.quantity));
      setMode('accept');
      setOfferPricePerUnit(String(item.unit_discount_price ?? item.discount_price));
      setDeliveryChoice(item.delivery_mode === 'delivery' ? 'delivery' : 'pickup');
    }
  }, [item]);

  if (!item) return null;

  const unitPrice = item.unit_discount_price ?? item.discount_price;
  const unitOriginal = item.unit_original_price ?? item.original_price;
  const numericQty = parseInt(qty) || 0;
  const numericOfferPrice = parseFloat(offerPricePerUnit) || 0;
  const validQty = numericQty >= 1 && numericQty <= item.quantity;
  const finalDelivery = item.delivery_mode === 'either' ? deliveryChoice : item.delivery_mode;
  const transportFee = finalDelivery === 'delivery' ? Number(item.delivery_fee || 0) : 0;
  const sellerTotal = numericQty * unitPrice + transportFee;
  const offerTotal = numericQty * numericOfferPrice + transportFee;

  const canSubmit =
    validQty &&
    (mode === 'accept' || (mode === 'offer' && numericOfferPrice > 0));

  const handleSubmit = () => {
    if (!canSubmit) return;
    if (mode === 'accept') {
      onAcceptPrice(numericQty, finalDelivery);
    } else {
      onCounterOffer(numericQty, numericOfferPrice, finalDelivery);
    }
  };

  return (
    <Dialog open={!!item} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="surplus-purchase-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Comprar excedente</DialogTitle>
          <DialogDescription>
            {item.product} — quedan {item.quantity} {item.format}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 space-y-1">
            <p>Precio original: <span className="line-through">€{unitOriginal.toFixed(2)}</span> / {item.format}</p>
            <p>Precio del vendedor: <span className="text-emerald-700 font-bold" data-testid="purchase-unit-price">€{unitPrice.toFixed(2)}</span> / {item.format} <span className="text-red-600 font-semibold">(-{item.discount_percentage}%)</span></p>
            {item.pricing_mode === 'per_lot' && (
              <p className="text-slate-500">El vendedor publicó el lote completo por €{item.discount_price.toFixed(2)}; el precio por {item.format} se calcula proporcionalmente.</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>¿Cuánto quieres comprar?</Label>
            <Input
              data-testid="purchase-quantity-input"
              type="number"
              min={1}
              max={item.quantity}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder={`Máx ${item.quantity}`}
            />
            {!validQty && qty !== '' && (
              <p className="text-xs text-red-600">
                Introduce una cantidad entre 1 y {item.quantity}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Entrega</Label>
            {item.delivery_mode === 'either' ? (
              <div className="grid grid-cols-2 gap-2" data-testid="delivery-choice">
                <button
                  type="button"
                  data-testid="delivery-choice-delivery"
                  onClick={() => setDeliveryChoice('delivery')}
                  className={`p-3 rounded-lg border-2 text-left transition-colors duration-200 ${deliveryChoice === 'delivery' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                >
                  <p className="text-xs font-bold text-slate-900">🚚 Entrega a domicilio</p>
                  <p className="text-xs text-slate-500 mt-1">{Number(item.delivery_fee || 0) > 0 ? `+ €${Number(item.delivery_fee).toFixed(2)} transporte` : 'Sin coste de transporte'}</p>
                </button>
                <button
                  type="button"
                  data-testid="delivery-choice-pickup"
                  onClick={() => setDeliveryChoice('pickup')}
                  className={`p-3 rounded-lg border-2 text-left transition-colors duration-200 ${deliveryChoice === 'pickup' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
                >
                  <p className="text-xs font-bold text-slate-900">🏬 Recogida</p>
                  <p className="text-xs text-slate-500 mt-1">Recoges en el proveedor</p>
                </button>
              </div>
            ) : (
              <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="delivery-fixed">
                🚚 {item.delivery_mode === 'delivery' ? 'Entrega a domicilio' : 'Recogida en el proveedor'}
                {item.delivery_mode === 'delivery' && Number(item.delivery_fee || 0) > 0 && ` · + €${Number(item.delivery_fee).toFixed(2)} transporte`}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Precio</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="mode-accept-price"
                onClick={() => setMode('accept')}
                className={`p-3 rounded-lg border-2 text-left transition-all duration-200 ${
                  mode === 'accept' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <p className="text-xs font-bold text-slate-900">Aceptar precio</p>
                <p className="text-xs text-slate-500 mt-1">€{unitPrice.toFixed(2)} / {item.format}</p>
              </button>
              <button
                type="button"
                data-testid="mode-counter-offer"
                onClick={() => setMode('offer')}
                className={`p-3 rounded-lg border-2 text-left transition-all duration-200 ${
                  mode === 'offer' ? 'border-emerald-600 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <p className="text-xs font-bold text-slate-900">Hacer una oferta</p>
                <p className="text-xs text-slate-500 mt-1">Propones tu precio</p>
              </button>
            </div>
          </div>

          {mode === 'offer' && (
            <div className="space-y-2">
              <Label>Tu precio por {item.format} (€)</Label>
              <Input
                data-testid="offer-price-input"
                type="number"
                min="0.01"
                step="0.01"
                value={offerPricePerUnit}
                onChange={(e) => setOfferPricePerUnit(e.target.value)}
                placeholder={unitPrice.toFixed(2)}
              />
              <p className="text-xs text-slate-500">
                Si tu oferta es aceptada, el vendedor te contactará. Puedes ofrecer más o menos que el precio publicado.
              </p>
            </div>
          )}

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-700">Total {mode === 'accept' ? '(precio del vendedor)' : '(tu oferta)'}:</span>
              <span className="text-lg font-bold text-emerald-700" data-testid="purchase-total">
                €{(mode === 'accept' ? sellerTotal : offerTotal).toFixed(2)}
              </span>
            </div>
            <p className="text-xs text-slate-500">{numericQty || 0} {item.format} × €{(mode === 'accept' ? unitPrice : numericOfferPrice).toFixed(2)}{transportFee > 0 ? ` + €${transportFee.toFixed(2)} transporte` : ''}</p>
            <p className="text-xs text-slate-500 mt-1">100% gratis para el comprador.</p>
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="purchase-confirm-button"
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {mode === 'accept' ? 'Comprar ahora' : 'Enviar oferta'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RestaurantDashboard;
